import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { useConsentManagement } from '../hooks/usePrivacyQueries';
import { useSaveConsentRequest } from '../hooks/usePrivacyMutations';
import { PageHeader, Card, ContentCard, Switch, MessageBar, DataTable } from './ui';

interface DataProcessingActivity {
  id: string;
  title: string;
  description: string;
  fields: string;
  requiredConsent: boolean;
}

interface LocationState {
  subjectEmail: string;
  dataSubjectRequestId: string;
}

export const ConsentManagement: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;

  const [dataProcessingActivities, setDataProcessingActivities] = useState<DataProcessingActivity[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const subjectEmail = state?.subjectEmail || '';
  const dataSubjectRequestId = state?.dataSubjectRequestId || '';

  const { data: consentData, isLoading } = useConsentManagement(
    subjectEmail,
    !!(state && subjectEmail && dataSubjectRequestId)
  );
  const saveConsentMutation = useSaveConsentRequest();

  useEffect(() => {
    if (!state || !state.subjectEmail || !state.dataSubjectRequestId) {
      navigate('/', { replace: true });
    }
  }, [state, navigate]);

  useEffect(() => {
    if (consentData?.dataProcessingActivities) {
      setDataProcessingActivities(consentData.dataProcessingActivities);
    }
  }, [consentData]);

  const handleSwitchInputChange = (data: DataProcessingActivity, checked: boolean) => {
    setDataProcessingActivities((prev) =>
      prev.map((item) =>
        item.id === data.id ? { ...item, requiredConsent: checked } : item
      )
    );
  };

  const backToPrivacyCenter = () => {
    navigate('/', { replace: true });
  };

  const saveConsentData = async () => {
    const model = {
      dataSubjectRequestId: dataSubjectRequestId,
      subjectEmail: subjectEmail,
      dataProcessingActivities: dataProcessingActivities.map((item) => ({
        id: item.id,
        requiredConsent: item.requiredConsent,
      })),
    };

    saveConsentMutation.mutate(model, {
      onSuccess: (data) => {
        if (!data.isError) {
          setSuccessMessage(data.message || 'Consent preferences saved successfully');
          setTimeout(() => setSuccessMessage(null), 10000);
          navigate('/', {
            state: {
              message:
                'Your consent request submitted successfully. Now we will proceed your data as per your consent.',
            },
            replace: true,
          });
        } else {
          setErrorMessage(data.responseException?.exceptionMessage || 'Failed to save consent preferences');
          setTimeout(() => setErrorMessage(null), 10000);
        }
      },
      onError: () => {
        setErrorMessage('Failed to save consent preferences');
        setTimeout(() => setErrorMessage(null), 10000);
      },
    });
  };

  const columns: ColumnDef<DataProcessingActivity, any>[] = [
    {
      accessorKey: 'title',
      header: 'Data Processing Activity',
    },
    {
      accessorKey: 'description',
      header: 'Description',
    },
    {
      accessorKey: 'fields',
      header: 'Data Classes',
    },
    {
      id: 'consent',
      header: 'Do You Consent?',
      cell: ({ row }) => (
        <Switch
          onText="Yes"
          offText="No"
          onCheckedChange={(checked) => handleSwitchInputChange(row.original, checked)}
          defaultChecked={row.original.requiredConsent}
        />
      ),
    },
  ];

  return (
    <div>
      <PageHeader title="Data Subject Request" />

      {/* Messages */}
      {successMessage && (
        <div className="mb-4">
          <MessageBar variant="success">{successMessage}</MessageBar>
        </div>
      )}
      {errorMessage && (
        <div className="mb-4">
          <MessageBar variant="error">{errorMessage}</MessageBar>
        </div>
      )}

      {/* Consent content */}
      {consentData?.content && (
        <ContentCard html={consentData.content} className="mb-6" />
      )}

      {/* Action buttons */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={saveConsentData}
          disabled={saveConsentMutation.isPending}
          className="bg-primary text-white hover:bg-primary/90 rounded-lg px-6 py-2.5 text-sm font-medium transition-colors disabled:opacity-50"
        >
          Confirm
        </button>
        <button
          onClick={backToPrivacyCenter}
          className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
      </div>

      {/* Data Processing Activities Table */}
      <Card className="p-4 sm:p-6 overflow-x-auto">
        <DataTable
          data={dataProcessingActivities}
          columns={columns}
          isLoading={isLoading}
          shimmerLines={5}
        />
      </Card>
    </div>
  );
};
